import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface AdditionalError {
  issue: string;
  message: string;
}

export const useErrorMap = (additionalErrors?: AdditionalError[]) => {
  const { t } = useTranslation('validations');
  return useMemo(() => {
    const errorMap: z.ZodErrorMap = (issue) => {
      switch (issue.code) {
        case z.ZodIssueCode.custom: {
          const params = issue.params as { issue?: string; characters?: string[] } | undefined;

          switch (params?.issue) {
            case 'invalid_characters':
              return t('invalid_characters', { characters: [...(params.characters ?? [])].join(', ') });
            case 'digit_required':
              return t('digit_required');
            case 'invalid_recipient':
              return t('invalid_recipient');
            case 'invalid_amount':
              return t('invalid_amount');
            case 'invalid_format':
              return t('invalid_format');
            case 'invalid_tax_number':
              return t('invalid_tax_number');
            case 'invalid_vat_number':
              return t('invalid_vat_number');
            case 'tax_or_vat_required':
              return t('tax_vat_number_required');
            case 'required':
              return t('required');
            case 'password_too_weak':
              return t('password_too_weak');
            case 'self_invoice_country_italy':
              return t('self_invoice_country_italy');
            case 'self_invoice_country_foreign':
              return t('self_invoice_country_foreign');
            case 'invalid_past_date':
              return t('invalid_past_date');
            case 'invalid_iban':
              return t('invalid_iban');
            case 'self_invoice_counterpart_same_as_company':
              return t('self_invoice_counterpart_same_as_company');
            case 'prefix_or_suffix_required':
              return t('prefix_or_suffix_required');
          }
          const matchingError = additionalErrors?.find((error) => error.issue === params?.issue);
          if (matchingError) {
            return matchingError.message;
          }
          break;
        }
        case z.ZodIssueCode.too_small: {
          if (issue.origin === 'number' && issue.minimum === 0) {
            return t('nonnegative');
          }
          if (issue.origin === 'string') {
            if (issue.minimum === 1) {
              return t('required');
            } else if (issue.exact) {
              return t('exact_string', { count: Number(issue.minimum) });
            } else {
              return t('minimum_string', { minimum: issue.minimum });
            }
          }
          return t('required');
        }
        case z.ZodIssueCode.too_big: {
          if (issue.origin === 'number') {
            return t('maximum', { maximum: issue.maximum });
          }
          if (issue.origin === 'string') {
            if (issue.exact) {
              return t('exact_string', { count: Number(issue.maximum) });
            }
            return t('maximum_string', { maximum: Number(issue.maximum) });
          }
          return t('required');
        }
        case z.ZodIssueCode.invalid_type:
          return t('required');
        case z.ZodIssueCode.invalid_format: {
          if (issue.format === 'email') return t('invalid_email');
          return t('invalid_format');
        }
        case z.ZodIssueCode.invalid_value:
          if (issue.input === '' || issue.input === null || issue.input === undefined) {
            return t('required');
          }
          return t('invalid_enum_value', {
            options: issue.values,
            value:
              typeof issue.input === 'string' ||
              typeof issue.input === 'number' ||
              typeof issue.input === 'boolean' ||
              typeof issue.input === 'bigint'
                ? String(issue.input)
                : JSON.stringify(issue.input),
          });
        default:
          break;
      }
      return undefined;
    };
    return errorMap;
  }, [additionalErrors, t]);
};
